import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import GoogleButton from "@/auth/GoogleButton";
import { ArrowRight, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

export default function JoinCompany() {
  const [params] = useSearchParams();
  const { hydrateSession } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [code, setCode] = useState(params.get("code") || "");
  const [verified, setVerified] = useState(null); // null | {company_name, role, invited_by}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [f, setF] = useState({
    first_name: "", last_name: "", email: "", phone: "", password: "", date_of_birth: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (params.get("code")) verify(); }, []);

  const verify = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get(`/invitations/verify/${encodeURIComponent(code.toUpperCase())}`);
      setVerified(data);
      setStep(2);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || t("invalid_code"));
      setVerified(false);
    } finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/join", { code: code.toUpperCase(), ...f });
      // Fase 6.5C — session cookies (access/refresh/csrf) are set by the
      // backend on this endpoint. No client-side token persistence.
      // Fase 6.5B — hydrateSession also fetches /legal/status so a
      // freshly-joined employee lands on LegalAcceptance if the Terms /
      // Privacy versions are still stale on their new user doc.
      await hydrateSession(data.user);
      toast.success(`${t("welcome_team")} — ${verified.company_name}`);
      navigate(["manager","teamlead","owner"].includes(data.user.role) ? "/dashboard" : "/my-roster", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] relative overflow-hidden">
      <div className="hero-mesh" aria-hidden />
      <nav className="relative z-10 max-w-6xl mx-auto flex items-center justify-between px-6 py-6">
        <Link to="/login" className="font-display text-[24px] font-bold tracking-tight text-[hsl(var(--text-strong))]">Rostr</Link>
        <Link to="/login" className="text-sm text-[hsl(var(--text-muted))] hover:text-[hsl(var(--text))]">{t("already_account")}</Link>
      </nav>

      <div className="relative z-10 max-w-xl mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="eyebrow mb-3">{t("join_company_title")}</div>
          <h1 className="font-display text-[36px] leading-[1.05] tracking-tight text-[hsl(var(--text-strong))]">
            {step === 1 ? t("fill_invite") : t("create_account")}
          </h1>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-6 md:p-8 mt-8">
          {step === 1 && (
            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-1.5">
                <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{t("invitation_code")}</span>
                <input
                  data-testid="jc-code" value={code} onChange={(e)=>setCode(e.target.value.toUpperCase())}
                  placeholder="RST-XXXX-XXXX"
                  className="input-field font-mono text-center tracking-[0.3em] text-[16px]"
                />
              </label>
              {error && (
                <div className="text-sm text-[hsl(var(--danger))] bg-[hsl(var(--danger-soft))] px-3 py-2 rounded-xl flex items-center gap-2">
                  <XCircle size={14}/> {error}
                </div>
              )}
              <button
                onClick={verify} disabled={busy || !code}
                data-testid="jc-verify" className="btn btn-primary"
              >{busy ? t("checking") : t("verify_code")} <ArrowRight size={14} /></button>
            </div>
          )}

          {step === 2 && verified && (
            <>
              <div className="bg-[hsl(var(--success-soft))] px-4 py-3 rounded-xl mb-5 flex items-center gap-3">
                <CheckCircle2 size={18} className="text-[hsl(var(--success))]" />
                <div className="flex-1">
                  <div className="text-[13px] font-semibold text-[hsl(var(--text-strong))]">{verified.company_name}</div>
                  <div className="text-[11px] text-[hsl(var(--text-muted))]">{t("role")}: <b>{verified.role}</b> · {t("invited_by")} {verified.invited_by || "—"}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("first_name")}>
                  <input data-testid="jc-first" value={f.first_name} onChange={(e)=>setF({...f,first_name:e.target.value})} className="input-field" />
                </Field>
                <Field label={t("last_name")}>
                  <input data-testid="jc-last" value={f.last_name} onChange={(e)=>setF({...f,last_name:e.target.value})} className="input-field" />
                </Field>
                <div className="md:col-span-2">
                  <GoogleButton
                    intent="join"
                    dataTestid="jc-google"
                    disabled={!f.first_name || !f.last_name}
                    payload={{
                      code: code.toUpperCase(),
                      first_name: f.first_name,
                      last_name: f.last_name,
                      phone: f.phone || null,
                      date_of_birth: f.date_of_birth || null,
                    }}
                  />
                  <div className="text-[11px] text-[hsl(var(--text-muted))] mt-2 text-center">
                    {t("fastest_option")}
                  </div>
                </div>
                <div className="md:col-span-2 flex items-center gap-3 my-1">
                  <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                  <span className="text-[10px] uppercase tracking-[0.14em] text-[hsl(var(--text-muted))]">{t("or_email_password")}</span>
                  <div className="flex-1 h-px bg-[hsl(var(--border))]" />
                </div>
                <Field label={t("email_addr")} className="md:col-span-2">
                  <input type="email" data-testid="jc-email" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} className="input-field" />
                </Field>
                <Field label={t("phone")}>
                  <input data-testid="jc-phone" value={f.phone} onChange={(e)=>setF({...f,phone:e.target.value})} className="input-field" />
                </Field>
                <Field label={t("birthday_field")}>
                  <input type="date" data-testid="jc-birthday" value={f.date_of_birth} onChange={(e)=>setF({...f,date_of_birth:e.target.value})} className="input-field" />
                </Field>
                <Field label={t("password")} className="md:col-span-2">
                  <input type="password" data-testid="jc-password" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} className="input-field" placeholder={t("min_6_chars")} />
                </Field>
                {error && (<div className="md:col-span-2 text-sm text-[hsl(var(--danger))] bg-[hsl(var(--danger-soft))] px-3 py-2 rounded-xl">{error}</div>)}
                <div className="md:col-span-2 flex justify-between mt-2">
                  <button onClick={()=>{setStep(1); setVerified(null);}} className="btn btn-quiet">{t("back")}</button>
                  <button onClick={submit} disabled={busy || !f.email || !f.password || !f.first_name}
                    data-testid="jc-submit" className="btn btn-primary">
                    {busy ? t("working") : t("joining")} <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, children, className="" }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      <span className="text-[12px] font-medium text-[hsl(var(--text-muted))]">{label}</span>
      {children}
    </label>
  );
}
